import { isNil, pick } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configDatabaseRecordType,
  configIdType,
  configPageableMetaType,
  configSystemRecordType,
  IDatabaseRecord,
  IId,
  IPageable,
  IPartialPageable,
  ISystemRecord,
} from "@/core/api";
import { dateTimeType, dateType, TDate, TDateTime } from "@/core/dates";
import { htmlType, THtml } from "@/core/html";
import { attachmentType, IAttachment } from "@/data/attachments";
import { codelistType, ICodelist } from "@/data/codelists";
import { companyType, ICompany } from "@/data/companies";
import { contactPersonType, IContactPerson, TWorkStyleType } from "@/data/consultant-profiles";
import { customFieldType, ICustomField } from "@/data/custom-fields";
import { EOriginSystemOwner } from "@/data/job-requests/types/JobRequestOrigin";
import { JobRequestPayExpressConfig } from "@/data/job-requests/types/JobRequestPayExpressConfig";
import { ILanguageWithLevel, languageWithLevelType } from "@/data/languages";
import { ILocation, locationType } from "@/data/locations";
import { IPhone, phoneType } from "@/data/phones";
import { ILegalEntityShort, legalEntityShortType } from "@/data/public-profiles";
import { ISkillCategory, ISkillWithPriority, skillCategoryType, skillWithPriorityType } from "@/data/skills";

export interface IDesignatedCandidate extends IId, IDatabaseRecord {
  companyContactPerson: string;
  companyEmail: string;
  companyName: string;
  companyPhoneNumber: IPhone;
  expectedRate: number;
  motivation: string;
  type: string;
  workerName: string;
}

const configDesignatedCandidateType: TType<IDesignatedCandidate> = {
  ...configIdType,
  ...configDatabaseRecordType,
  companyContactPerson: stringType(),
  companyEmail: stringType(),
  companyName: stringType(),
  companyPhoneNumber: phoneType,
  expectedRate: numberType(),
  motivation: stringType(),
  type: stringType(),
  workerName: stringType(),
};

export const designatedCandidateType = ensure(configDesignatedCandidateType);

export interface IJobRequestConsent {
  content: THtml;
  dateFrom: TDate;
  dateTo: TDate;
  referenceId: number;
  version: number;
}

const configJobRequestConsentType: TType<IJobRequestConsent> = {
  content: htmlType(""),
  dateFrom: dateType(),
  dateTo: dateType(),
  referenceId: numberType(),
  version: numberType(),
};

export const jobRequestConsentType = ensure(configJobRequestConsentType);

export interface IQuestion extends IId {
  question: string;
  mandatory: boolean;
}

const configQuestionType: TType<IQuestion> = {
  ...configIdType,
  question: stringType(""),
  mandatory: booleanType(false),
};

export const questionType = ensure(configQuestionType);

export interface IOriginDetail {
  logo: string;
  color: string;
  homePageLink: string;
  vatNumber: string;
}

const configOriginDetailType: TType<IOriginDetail> = {
  logo: stringType(""),
  color: stringType(""),
  homePageLink: stringType(""),
  vatNumber: stringType(""),
};

const originDetailType = ensure(configOriginDetailType);

export interface IOrigin extends IDatabaseRecord, ISystemRecord {
  name: string;
  publicProfileSlug: string;
  details: IOriginDetail;
  parent: Omit<IOrigin, "parent"> | null;
  systemOwner: EOriginSystemOwner | null;
}

const configOriginType: TType<IOrigin> = {
  ...configDatabaseRecordType,
  ...configSystemRecordType,
  name: stringType(""),
  publicProfileSlug: stringType(""),
  details: v => (isNil(v) ? null : originDetailType(v)),
  parent: v => (isNil(v) ? null : originType(v)),
  systemOwner: stringType(null),
};

export const originType = ensure(configOriginType);

export enum EJobRequestStatus {
  Open = "OPEN",
  Closed = "CLOSED",
}

export interface IJobRequestRate {
  currency: string;
  maxRate: number;
  clientRateType: "HOURLY" | "FIXED" | null;
}

const configJobRequestRateType: TType<IJobRequestRate> = {
  currency: stringType(""),
  maxRate: numberType(0),
  clientRateType: stringType(""),
};

const jobRequestRateType = ensure(configJobRequestRateType);

export interface IJobRequestClient {
  name: string;
  logo: IAttachment | null;
}

const configJobRequestClientType: TType<IJobRequestClient> = {
  name: stringType(""),
  logo: v => (isNil(v) ? null : attachmentType(v)),
};

export const jobRequestClientType = ensure(configJobRequestClientType);

export interface ISkillRole extends IId {
  name: string;
}

const configJobRequestSkillRoleType: TType<ISkillRole> = {
  ...configIdType,
  name: stringType(""),
};

export const jobRequestSkillRoleType = ensure(configJobRequestSkillRoleType);

export interface IBrokerFee {
  description: string | null;
  percentage: number | null;
}

const configBrekerFeeType: TType<IBrokerFee> = {
  description: stringType(null),
  percentage: numberType(null),
};

export const brokerFeeType = ensure(configBrekerFeeType);

const configJRPayExpressConfigType: TType<JobRequestPayExpressConfig> = {
  defaultPayExpress: booleanType(false),
  payExpressEnabled: booleanType(false),
  payExpressFeePercentage: numberType(null),
};
export const JRPayExpressConfigType = ensure(configJRPayExpressConfigType);

export interface IJobRequest extends IDatabaseRecord, ISystemRecord {
  alreadyApplied: boolean;
  attachments: IAttachment[];
  businessUnitName?: string;
  company?: ICompany; // TODO IBaseCompany? TBC
  customFields: ICustomField[];
  description?: THtml;
  endDate?: TDate | null;
  endDateOpen?: boolean;
  firstDayOfApplications?: TDateTime | null;
  hoursPerWeek: number | null;
  jobApplicationCount: number;
  languages: ILanguageWithLevel[];
  lastDayOfApplications?: TDateTime | null;
  legalEntityName: string | null;
  locations?: ILocation[];
  numberOfPositions: number | null;
  origin: IOrigin;
  public: boolean;
  rate: IJobRequestRate;
  businessReferenceId?: string | null;
  brokerFee: IBrokerFee | null;
  remoteness?: number | null;
  requirements?: THtml;
  skills: ISkillWithPriority[];
  skillCategory: ISkillCategory | null;
  status: EJobRequestStatus;
  startDate?: TDate | null;
  startDateAsap?: boolean;
  title?: string;
  workload?: ICodelist;
  hiringManager?: IContactPerson;
  questions?: IQuestion[];
  favourite?: boolean;
  promoteClient?: boolean;
  client?: IJobRequestClient;
  managedByDescription?: THtml;
  skillRoleDescription?: THtml;
  level?: string;
  levelDescription?: THtml;
  legalEntityVatNo?: string;
  skillRole?: ISkillRole;
  designatedCandidate?: IDesignatedCandidate;
  workStyles?: TWorkStyleType[];
  legalEntityClient?: ILegalEntityShort | null;
  administratorLegalEntityClient?: ILegalEntityShort | null;
  paymentTerm: number | null;
  supplierPaymentTerm: number | null;
  payExpressConfig: JobRequestPayExpressConfig | null;
  distributionDate?: TDateTime | null;
}

export const configJobRequestType: TType<IJobRequest> = {
  ...configDatabaseRecordType,
  ...configSystemRecordType,
  alreadyApplied: booleanType(false),
  attachments: ensureArray(attachmentType),
  businessUnitName: v => (isNil(v) ? null : stringType("")(v)),
  company: v => (isNil(v) ? null : companyType(v)),
  customFields: ensureArray(customFieldType),
  description: htmlType(""),
  endDate: dateType(),
  endDateOpen: booleanType(false),
  firstDayOfApplications: dateTimeType(),
  hoursPerWeek: v => (isNil(v) ? null : numberType(0)(v)),
  jobApplicationCount: numberType(0),
  languages: ensureArray(languageWithLevelType),
  lastDayOfApplications: dateTimeType(),
  legalEntityName: v => (isNil(v) ? null : stringType("")(v)),
  locations: ensureArray(locationType),
  numberOfPositions: v => (isNil(v) ? null : numberType(1)(v)),
  origin: originType,
  public: booleanType(false),
  rate: jobRequestRateType,
  businessReferenceId: v => (isNil(v) ? null : stringType(null)(v)),
  brokerFee: v => (isNil(v) ? null : brokerFeeType(v)),
  remoteness: v => (isNil(v) ? null : numberType(null)(v)),
  requirements: htmlType(""),
  skills: ensureArray(skillWithPriorityType),
  skillCategory: v => (isNil(v) ? null : skillCategoryType(v)),
  status: stringType(EJobRequestStatus.Open),
  startDate: dateType(),
  startDateAsap: booleanType(false),
  title: stringType(""),
  workload: v => (isNil(v) ? null : codelistType(v)),
  hiringManager: v => (isNil(v) ? null : contactPersonType(v)),
  questions: ensureArray(questionType),
  favourite: booleanType(false),
  promoteClient: booleanType(false),
  client: v => (isNil(v) ? null : jobRequestClientType(v)),
  managedByDescription: stringType(""),
  skillRoleDescription: htmlType(),
  level: stringType(),
  levelDescription: htmlType(),
  legalEntityVatNo: stringType(),
  skillRole: jobRequestSkillRoleType,
  workStyles: v => (isNil(v) || !v.length ? [] : ensureArray(stringType(null))),
  designatedCandidate: v => (isNil(v) ? null : designatedCandidateType(v)),
  legalEntityClient: v => (isNil(v) ? null : legalEntityShortType(v)),
  administratorLegalEntityClient: v => (isNil(v) ? null : legalEntityShortType(v)),
  paymentTerm: v => (isNil(v) ? null : v),
  supplierPaymentTerm: v => (isNil(v) ? null : v),
  payExpressConfig: v => (isNil(v) ? null : JRPayExpressConfigType(v)),
  distributionDate: dateTimeType(),
};

export const jobRequestType = ensure(configJobRequestType);

export interface IPageableJobRequest extends IPageable<IJobRequest[]> {}

export const pageableJobRequestTypeConfig: TType<IPageableJobRequest> = {
  ...configPageableMetaType,
  content: ensureArray(jobRequestType),
};

export const pageableJobRequestType = ensure(pageableJobRequestTypeConfig);

export interface IPartialPageableJobRequest extends IPartialPageable<IJobRequest[]> {}

export interface IShortJobRequest
  extends Pick<
    IJobRequest,
    | "id"
    | "title"
    | "locations"
    | "rate"
    | "client"
    | "promoteClient"
    | "remoteness"
    | "origin"
    | "legalEntityClient"
    | "administratorLegalEntityClient"
  > {}

const configShortJobRequestType: TType<IShortJobRequest> = pick(configJobRequestType, [
  "id",
  "title",
  "locations",
  "rate",
  "client",
  "promoteClient",
  "remoteness",
  "origin",
  "legalEntityClient",
  "administratorLegalEntityClient",
]);

export const shortJobRequestType = ensure(configShortJobRequestType);

export interface IPageableShortJobRequest extends IPageable<IShortJobRequest[]> {}

export const pageableShortJobRequestTypeConfig: TType<IPageableShortJobRequest> = {
  ...configPageableMetaType,
  content: ensureArray(shortJobRequestType),
};

export const pageableShortJobRequestType = ensure(pageableShortJobRequestTypeConfig);
